import { Injectable, signal } from "@angular/core";
import { ChatMessage } from "../models/message";
import { SessionSummary } from "../models/session";

/**
 * Survives `AvioblueChatbotComponent` being destroyed and recreated, which happens on every Floating ↔
 * Sidebar switch and on every minimize/reopen, since hosts toggle those with `@if`/`@else`,
 * not `[hidden]`. Without this, each toggle reset
 * `chatMessages` to `[]` and re-fetched the sidebar list from the server, discarding whatever
 * conversation was open. Kept as a `providedIn: 'root'` singleton so it lives for the app's
 * lifetime, not the component's.
 */
@Injectable({ providedIn: "root" })
export class ChatWidgetStateService {
    readonly sessions = signal<SessionSummary[]>([]);

    /** Plain array, not a signal: `AvioblueChatbotComponent` reassigns it wholesale and drives rendering with
     *  its own `ChangeDetectorRef.detectChanges()` calls (the app runs zoneless), the same pattern
     *  as before this was lifted out of the component. */
    chatMessages: ChatMessage[] = [];

    /** Set once `initializeSessions()` has run. Lets `AvioblueChatbotComponent.ngOnInit` skip re-running it,
     *  and therefore skip the network round-trip and the reset to the greeting message, on every
     *  remount within the same app lifetime. */
    initialized = false;

    /** The last `presetAgent` value actually applied (the agent configuration page's "Test"
     *  button). Also lifted out of
     *  `AvioblueChatbotComponent`: left as a component field, a remount would forget it and restart the test
     *  conversation on every Floating ↔ Sidebar switch even though the preset hasn't changed. */
    lastAppliedPresetAgent: string | null = null;

    /**
     * The composer's unsent text: closing the widget mid-draft (the top-level X, the
     * host's navbar toggle, a Floating ↔ Sidebar switch) unmounts `<avioblue-chatbot>` just like
     * every other close, and `kendo-chat`'s own composer state does not survive that. Lifted out
     * here for the same reason as `chatMessages`, and bound two-way to `kendo-chat`'s own public
     * `[(inputValue)]` in `main.component.html`, with no DOM-poking into Kendo internals required.
     * (A Kendo Splitter pane's `collapsed` state was tried first as a way to keep the component
     * itself alive across a close instead, but
     * `SplitterPaneComponent`'s template is `@if (!collapsed) { <ng-content> }`: it destroys the
     * projected content exactly like removing the component would, so it solved nothing.)
     */
    draftMessage = "";
}
